import React, { useEffect, useMemo, useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import DTSelectedItemTransactionView from '../../DTSelectedItemTransactionView';
import { changeOrderAddress } from '../../../../../../actions/tools/CommonAsyncOrderActions';
import { shopifyChangeOrderAddress } from '../../../../../../actions/tools/ShopifyActions';
import { returnToPreviousMacroWizardPath } from '../../../../../../actions/searchActions';
import {
  getShippingAddressOfSelectedOrder,
  getOrderingSystem,
  getSelectedOrder,
} from '../../../../../../selectors/tools';
import { withConfirmation } from '../../../../DTConfirmationView/withConfirmation';
import { HeaderContainer, Title } from '../../../../DecisionTree.styles';
import { Input, Label, Row, FormContainer } from './DTChangeOrderAddressNode.styles';
import { camelToSnakeCase } from '../../../../../../util/util';

const FORM_FIELDS_MAP = Object.freeze({
  firstName: 'First name',
  lastName: 'Last name',
  address1: 'Address line 1',
  address2: 'Address line 2',
  city: 'City',
  region: 'State/Region',
  zip: 'ZIP',
  country: 'Country',
  phone: 'Phone',
});

const DTChangeOrderAddressNode = () => {
  const dispatch = useDispatch();
  const orderShippingAddress = useSelector(getShippingAddressOfSelectedOrder);
  const orderSystem = useSelector(getOrderingSystem);
  const selectedOrder = useSelector(getSelectedOrder);
  const [formData, setFormData] = useState({});

  const prePopulatedData = useMemo(() => {
    const getAddressValue = (key) => {
      if (orderSystem === 'shopify') {
        if (key === 'region') {
          return orderShippingAddress['province'];
        }

        return orderShippingAddress[camelToSnakeCase(key)] ?? '';
      }

      if (key === 'region') {
        return orderShippingAddress['region'] ?? orderShippingAddress['state'] ?? '';
      }

      return orderShippingAddress[key] ?? '';
    };

    return {
      firstName: getAddressValue('firstName'),
      lastName: getAddressValue('lastName'),
      address1: orderShippingAddress.address1 ?? '',
      address2: orderShippingAddress.address2 ?? '',
      city: orderShippingAddress.city ?? '',
      region: getAddressValue('region'),
      zip: orderShippingAddress.zip ?? '',
      country: orderShippingAddress.country ?? '',
      phone: orderShippingAddress.phone ?? '',
    };
  }, [orderShippingAddress, orderSystem]);

  useEffect(() => {
    setFormData(prePopulatedData);
  }, [prePopulatedData]);

  useLayoutEffect(() => {
    if (isEmpty(selectedOrder)) {
      dispatch(returnToPreviousMacroWizardPath());
    }
  }, [dispatch, selectedOrder]);

  const handleInputChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleNextButtonClick = () => {
    if (orderSystem === 'shopify') {
      return dispatch(shopifyChangeOrderAddress(formData));
    }

    return dispatch(changeOrderAddress(formData));
  };

  return (
    <DTSelectedItemTransactionView
      nextButtonLabel="NEXT"
      showContainer={true}
      isNextEnabled={!isEqual(formData, prePopulatedData)}
      nextButtonClickHandler={handleNextButtonClick}
    >
      <HeaderContainer>
        <Title>Edit address</Title>
      </HeaderContainer>
      <FormContainer>
        {Object.keys(FORM_FIELDS_MAP).map((key) => (
          <Row key={key}>
            <Label htmlFor={key}>{FORM_FIELDS_MAP[key]}</Label>
            <Input id={key} name={key} value={formData[key] ?? ''} onChange={handleInputChange} />
          </Row>
        ))}
      </FormContainer>
    </DTSelectedItemTransactionView>
  );
};

export default withConfirmation(DTChangeOrderAddressNode);
